import React, { useCallback, useEffect, useRef } from 'react';
import UnitGroup from './UnitGroup';
import useSyncTabs from '../../Hooks/useSyncTabs';
import { useAuth } from '../../Contexts/AuthProvider';
import { useBoxes } from '../../Contexts/BoxContext';
import { unitChannel } from '../../utils/functions';
import uuid from 'uuid/v1';
import { boxHelper, dashboardHelper, uniqueValues } from '../../helpers';
import AlertToastr from '../../utils/alert';
import useTranslation from '../../Hooks/useTranslation';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCurrentCompany } from '../../Store/modules/dashboard/actions';
import { getUnitById } from '../../Hooks/useUnit';
import DmsService from '../../Services/DMS/dms-api.service';

const UnitsPage = () => {

    const { dashboardId } = useParams();
    const translation = useTranslation();
    const dispatch = useDispatch();
    const {
        userLoaded,
        win,
        setWin,
        initDashboardWindow,
        currentCompany,
        currentUser,
        isSignedFlow
    } = useAuth();
    const {
        unitBoxes,
        currentUnit,
        setCurrentUnit,
        setCurrentUnitModel,
        currentUnitModelFull,
        addUnitBox,
        editUnitBox,
        removeUnitBox,
        addUnitBoxes,
        unitBoxesFilters,
        setCurrentUnitTemplate,
        setUnitBoxesFilters,
        startDate,
        endDate,
        updateDateRange,
        changeUnitGlobalFilters,
        unitsRequest
    } = useBoxes();

    const { mutateAsync: syncTabs } = useSyncTabs('units');
    
    const chartCreatedRef = useRef(false);

    const syncTabsWithBothServers = useCallback((tabId, boxes, colors = []) => {
        if (!tabId) return;
        const parentTabId = win?.parentId === win?.id ? null : win?.parentId;
        
        // Sync with auth server
        syncTabs({
            tabId: tabId,
            items: boxes,
            companyId: currentCompany?.id,
        }).then();

        // Also sync with API server
        if (currentUser?.isSuperuser()) {
            if (currentCompany) {
                DmsService.syncTabWithApiServer(tabId, boxes, colors, currentCompany.id, parentTabId)
                    .then(response => {
                        // console.log(response)
                    })
                    .catch(error => {
                        // console.log(error)
                    });
            }
        } else {
            DmsService.syncTabWithApiServer(tabId, boxes, colors, undefined, parentTabId)
                .then(response => {
                    // console.log(response)
                })
                .catch(error => {
                    // console.log(error)
                });
        }
    }, [syncTabs, win, currentCompany, currentUser]);

    useEffect(() => {
        if (!dashboardId) return;

        initDashboardWindow();

        const newWinStorageKey = `dashboard-${dashboardId}`;
        const localStorageData = localStorage.getItem(newWinStorageKey);
        if (!localStorageData) {
            // console.log('No any dashboard data in storage');
            return;
        }

        const dashboardInfo = JSON.parse(localStorageData);
        // localStorage.removeItem(newWinStorageKey);
        const { box, parentDashboardId, company, unit } = dashboardInfo;
        if (company) dispatch(setCurrentCompany(company));
        if (unit) {
            setCurrentUnit(unit);
        }

        if (box) {
            const unitBoxes = Array.isArray(box)
                ? box
                : [box];
            addUnitBoxes(unitBoxes);
        }
        setWin({
            id: dashboardId,
            parentId: parentDashboardId ? parentDashboardId : null,
        });

    }, [
        dashboardId,
        initDashboardWindow,
        setWin,
        setCurrentUnit,
        addUnitBoxes,
        dispatch
    ]);

    useEffect(() => {
        if (!win?.id) return;
        const parentTabId = win.parentId === win.id ? null : win.parentId;
        
        syncTabs({
            tabId: win.id,
            items: unitBoxes,
            companyId: currentCompany?.id,
        }).then();

        
        if (currentUser?.isSuperuser()) {
            if (currentCompany) {
                DmsService.syncTabWithApiServer(win.id, unitBoxes, [], currentCompany.id, parentTabId)
                    .then(response => {
                        // console.log(response)
                    })
                    .catch(error => {
                        // console.log(error)
                    });
            }
        } else {
            DmsService.syncTabWithApiServer(win.id, unitBoxes, [], undefined, parentTabId)
                .then(response => {
                    // console.log(response)
                })
                .catch(error => {
                    // console.log(error)
                });
        }

    }, [win, unitBoxes, currentCompany, syncTabs, currentUser]);

    // Auto-set unit from allowedUnits after login
    useEffect(() => {
        if (!dashboardId && currentUser && currentUser.isOneUnitPassword() && isSignedFlow && !currentUnit) {
            const { data: units, isSuccess } = unitsRequest;
            // Only set unit if units are successfully loaded
            if (isSuccess && units && units.length > 0 && currentUser.allowedUnits && currentUser.allowedUnits.length > 0) {
                // Find unit by uId from allowedUnits
                const allowedUnitUId = currentUser.allowedUnits[0]; // Take first allowed unit
                const unitModel = units.find(unit => unit.uId === allowedUnitUId);
                
                if (unitModel) {
                    // Set unit by id if available, otherwise by uId
                    if (unitModel.id) {
                        setCurrentUnit(unitModel.id);
                    } else {
                        setCurrentUnit(unitModel.uId);
                    }
                    setCurrentUnitModel(unitModel);
                }
            }
        }
    }, [dashboardId, currentUser, isSignedFlow, currentUnit, unitsRequest, setCurrentUnit, setCurrentUnitModel]);

    // Auto-set unit from allowedUnits after page refresh
    useEffect(() => {
        if (!dashboardId && currentUser && currentUser.isOneUnitPassword() && !isSignedFlow && !currentUnit && userLoaded) {
            const { data: units, isSuccess } = unitsRequest;
            // Only set unit if units are successfully loaded
            if (isSuccess && units && units.length > 0 && currentUser.allowedUnits && currentUser.allowedUnits.length > 0) {
                // Find unit by uId from allowedUnits
                const allowedUnitUId = currentUser.allowedUnits[0]; // Take first allowed unit
                const unitModel = units.find(unit => unit.uId === allowedUnitUId);
                
                if (unitModel) {
                    // Set unit by id if available, otherwise by uId
                    if (unitModel.id) {
                        setCurrentUnit(unitModel.id);
                    } else {
                        setCurrentUnit(unitModel.uId);
                    }
                    setCurrentUnitModel(unitModel);
                }
            }
        }
    }, [dashboardId, currentUser, isSignedFlow, currentUnit, unitsRequest, setCurrentUnit, setCurrentUnitModel, userLoaded]);
  
    useEffect(() => {
        if (dashboardId) return; 
        
        try {
            const savedUnitId = localStorage.getItem('unitsPageUnitId');
            const savedDateFrom = localStorage.getItem('unitsPageDateFrom');
            const savedDateTo = localStorage.getItem('unitsPageDateTo');
            const savedFilters = localStorage.getItem('unitsPageFilters');
            const savedUnitModel = localStorage.getItem('unitsPageUnitModel');
            
            if (savedUnitId && savedDateFrom && savedDateTo && savedFilters) {
                const dateFrom = new Date(savedDateFrom);
                const dateTo = new Date(savedDateTo);
                const filters = JSON.parse(savedFilters);
                
                filters.dateFrom = dateFrom;
                filters.dateTo = dateTo;
                
                setCurrentUnit(savedUnitId);
                
                if (savedUnitModel) {
                    try {
                        const unitModel = JSON.parse(savedUnitModel);
                        setCurrentUnitModel(unitModel);
                    } catch (e) {
                        console.error('Failed to parse unitModel from localStorage:', e);
                    }
                }

                updateDateRange(dateFrom, dateTo);
                
                changeUnitGlobalFilters(filters);
                
                localStorage.removeItem('unitsPageUnitId');
                localStorage.removeItem('unitsPageDateFrom');
                localStorage.removeItem('unitsPageDateTo');
                localStorage.removeItem('unitsPageFilters');
                localStorage.removeItem('unitsPageUnitModel');
            }
        } catch (e) {
            console.error('Failed to read data from localStorage:', e);
        }
    }, [dashboardId, setCurrentUnit, setCurrentUnitModel, updateDateRange, changeUnitGlobalFilters]);

    const handleAddBox = (data, openInNewWindow = null) => {
        if (openInNewWindow) {
            const parentId = win.parentId || win.id;
            dashboardHelper.add({
                id: null,
                parent: parentId,
                box: data,
                job: null,
                unit: currentUnit,
                company: currentCompany,
                filters: unitBoxesFilters,
            }, 'unit');
        } else {
            addUnitBox(data);
        }
    };

    const handleEditBox = (data, openInNewWindow) => {
        if (openInNewWindow) {
            const parentId = win.parentId || win.id;
            dashboardHelper.add({
                id: null,
                parent: parentId,
                box: data,
                job: null,
                unit: currentUnit,
                company: currentCompany,
            }, 'unit');
            removeUnitBox(data.id);
        } else {
            editUnitBox(data);
        }
    };

    const handleRemoveBox = boxId => {
        removeUnitBox(boxId);
    };

    const handleAddBoxes = (boxes) => {
        const boxesNew = boxHelper.updateDragAndDropPosition(boxes);
        addUnitBoxes(boxesNew);
    };

    const handleTemplateLoad = async (tabs, template, unitModel) => {
        let isSuccessTemplateLoad = false;

        let globalFilterInit = null;

        let unitModelFull;
        if (unitModel) {
             unitModelFull = await getUnitById({
                id: unitModel.id,
                companyId: currentCompany?.id,
                section: 'units'
            })

        }
        const unitFull = unitModelFull || currentUnitModelFull;
        
        // Check if unitFull is available before proceeding
        if (!unitFull) {
            console.error('Unit data is not available for template loading');
            return;
        }

        tabs?.forEach(tab => {
            const boxes = tab.items;
            // console.log('boxes', boxes);


            const boxesForNewWindow = [];
            const boxesForParentWindow = [];

            boxes.forEach(templateBox => {

                const boxDataChannels = [];
                if (templateBox?.channels && unitFull)
                    templateBox?.channels?.forEach(channel => {
                        let chObj = unitChannel(unitFull, channel.code);
                        if (chObj) {
                            chObj = { ...chObj, unit: channel.unit };
                            boxDataChannels.push(chObj);
                        }
                    });

                let templateBoxMainXYChannel = '';
                if (templateBox.mainXYChannel) {
                    if (templateBox.mainXYChannel.id) {
                        templateBoxMainXYChannel = templateBox.mainXYChannel.id;
                    } else {
                        templateBoxMainXYChannel = templateBox.mainXYChannel;
                    }
                }

                if (
                    boxDataChannels.length > 0
                    || templateBox.type === 'eventLogger'
                    || templateBox.type === 'jobProperties'
                ) {
                    isSuccessTemplateLoad = true;

                    const templateBoxFilters = templateBox.filters || {};

                    if (templateBoxFilters && !globalFilterInit) {
                        globalFilterInit = {
                            dateType: templateBoxFilters.dateType || 'thisWeek',
                            jobId: templateBoxFilters.jobId || '',
                        }
                    }

                    const boxData = {
                        id: uuid(),
                        filters: templateBoxFilters,
                        name: templateBox.name || '',
                        type: templateBox.type,
                        chartType: templateBox.subType || '',
                        mainXYChannel: templateBoxMainXYChannel,
                        unitUid: unitFull.uId,
                        channels: boxDataChannels,
                        itemPosition: templateBox.itemPosition,
                        timeType: templateBox.timeType || 'local',
                        timeFormat: templateBox.timeFormat || 'dateTime',
                    };
                    addChartSettings(boxData, templateBox);
                    if (tab.isParent) {
                        // open box in current window
                        boxesForParentWindow.push(boxData);

                    } else {
                        // open box in new window
                        boxesForNewWindow.push(boxData);
                    }
                }
            });

            if (globalFilterInit) {
                setUnitBoxesFilters(globalFilterInit)
            }

            if (boxesForNewWindow.length) {
                handleAddBox(boxesForNewWindow, true);
            }

            if (boxesForParentWindow.length) {
                handleAddBoxes(boxesForParentWindow);
            }

        });

        if (template) {
            if (isSuccessTemplateLoad) {
                setCurrentUnitTemplate(template);
            } else {
                setCurrentUnitTemplate(null);
                AlertToastr.showErrorAlert(translation.notificationsLoadTemolateError);
            }
        }
    };

    const addChartSettings = useCallback((boxData, templateBox = null) => {
        if (boxData.channels && boxData.type === 'chartView') {
            const units = uniqueValues(boxData.channels.map(channel => channel.unit));
            if (templateBox && templateBox.yAxisSettings) {
                boxData.yAxisSettings = templateBox.yAxisSettings;
            } else {
                boxData.yAxisSettings = uniqueValues(units.map(unit => {
                    return {
                        unit: unit,
                        autoScaling: true,
                        min: undefined,
                        max: undefined,
                    };
                }));
            }

            if (templateBox && templateBox.xAxisSettings) {
                boxData.xAxisSettings = templateBox.xAxisSettings;
            } else {
                if (boxData.chartType === 'timeBased') {
                    boxData.xAxisSettings = {
                        count: undefined,
                        autoScaling: true,
                        unit: 'sec',
                    };
                } else {
                    const mainChannel = unitChannel(currentUnitModelFull, boxData.mainXYChannel);
                    boxData.xAxisSettings = {
                        unit: mainChannel.unit,
                        autoScaling: true,
                        min: undefined,
                        max: undefined,
                    };
                }
            }
        }
    }, [currentUnitModelFull]);
    
    useEffect(() => {
        chartCreatedRef.current = false;
    }, [currentUnit, unitBoxesFilters?.dateType]);

    useEffect(() => {
        if (chartCreatedRef.current) {
            return;
        }
        
        const existingChartBoxes = unitBoxes.filter(box => box.type === 'chartView');
        if (existingChartBoxes.length > 0 && !localStorage.getItem('healthCheckChannelsToOpen')) {
            chartCreatedRef.current = true;
            return;
        }
        
        if (dashboardId) {
            return;
        }
        
        const hasCustomRange = unitBoxesFilters?.dateType === 'customRange';
        const hasDateRange = (startDate && endDate) || (unitBoxesFilters?.dateFrom && unitBoxesFilters?.dateTo);
        const hasHealthCheckChannels = !!localStorage.getItem('healthCheckChannelsToOpen');
        
        const shouldCreateBox = unitBoxes.length === 0 || hasHealthCheckChannels;
        
        if (
            currentUnit &&
            hasCustomRange &&
            shouldCreateBox &&
            currentUnitModelFull &&
            hasDateRange
        ) {
            const allChannels = currentUnitModelFull.channels || [];
            
            if (allChannels.length > 0) {
                const dateFrom = unitBoxesFilters?.dateFrom || startDate;
                const dateTo = unitBoxesFilters?.dateTo || endDate;
                
                let selectedChannels = [];
                try {
                    const healthCheckChannels = localStorage.getItem('healthCheckChannelsToOpen');
                    if (healthCheckChannels) {
                        const channelCodes = JSON.parse(healthCheckChannels);
                        
                        const normalizedCodes = channelCodes.map(code => String(code).trim().toUpperCase());
                        
                        selectedChannels = allChannels.filter(ch => {
                            const channelCode = String(ch.code || '').trim().toUpperCase();
                            return normalizedCodes.includes(channelCode);
                        });
                        
                        localStorage.removeItem('healthCheckChannelsToOpen');
                    }
                } catch (e) {
                    console.error('Failed to read channels from localStorage:', e);
                }
                
                if (selectedChannels.length === 0) {
                    if (unitBoxes.length === 0) {
                        selectedChannels = allChannels.slice(0, 3);
                    } else {
                        return;
                    }
                }
                
                if (unitBoxes.length > 0 && selectedChannels.length > 0) {
                    const boxesToRemove = unitBoxes
                        .filter(box => box.type === 'chartView')
                        .map(box => box.id);
                    boxesToRemove.forEach(boxId => {
                        removeUnitBox(boxId);
                    });
                }
                
                chartCreatedRef.current = true;
                
                const boxData = {
                    id: uuid(),
                    filters: {
                        ...unitBoxesFilters,
                        dateFrom: dateFrom,
                        dateTo: dateTo,
                    },
                    name: '',
                    type: 'chartView',
                    chartType: 'timeBased',
                    mainXYChannel: '',
                    unitUid: currentUnitModelFull.uId,
                    channels: selectedChannels.map(ch => ({
                        ...ch,
                        unit: ch.unit
                    })),
                    itemPosition: {
                        rowIndex: 0,
                        position: 'Stretch',
                    },
                    timeType: 'local',
                    timeFormat: 'dateTime',
                };
                
                addChartSettings(boxData);
                
                addUnitBox(boxData);
            } else {
                chartCreatedRef.current = false;
            }
        }
    }, [
        currentUnit,
        unitBoxesFilters,
        unitBoxes,
        currentUnitModelFull,
        startDate,
        endDate,
        addUnitBox,
        addChartSettings,
        removeUnitBox,
        dashboardId
    ]);

    const handleOpenInNewWindow = (boxId) => {
        const movedBox = unitBoxes.find(box => box.id === boxId);
        if (movedBox) {
            const parentId = win.parentId || win.id;
            dashboardHelper.add({
                id: null,
                parent: parentId,
                box: movedBox,
                job: null,
                unit: currentUnit,
                company: currentCompany,
                filters: unitBoxesFilters
            }, 'unit');
            removeUnitBox(boxId);
        }
    };

    const handleUpdateSettings = useCallback(() => {
        if (!win?.id) return;
        const parentTabId = win.parentId === win.id ? null : win.parentId;
        
        syncTabs({
            tabId: win.id,
            items: unitBoxes,
            companyId: currentCompany?.id,
        }).then();

        // Also sync with API server
        if (currentUser?.isSuperuser()) {
            if (currentCompany) {
                DmsService.syncTabWithApiServer(win.id, unitBoxes, [], currentCompany.id, parentTabId)
                    .then(response => {
                        // console.log(response)
                    })
                    .catch(error => {
                        // console.log(error)
                    });
            }
        } else {
            DmsService.syncTabWithApiServer(win.id, unitBoxes, [], undefined, parentTabId)
                .then(response => {
                    // console.log(response)
                })
                .catch(error => {
                    // console.log(error)
                });
        }
    }, [currentCompany, syncTabs, unitBoxes, win, currentUser])

    if (dashboardId && !userLoaded) return null

    return (
        <div className="mainContainer">
            <UnitGroup
                onTemplateLoad={handleTemplateLoad}
                onAddBox={handleAddBox}
                onEditBox={handleEditBox}
                onRemoveBox={handleRemoveBox}
                onNewWindowOpen={handleOpenInNewWindow}
                syncTabs={syncTabsWithBothServers}
                onUpdateSettings={handleUpdateSettings}
            />
        </div>
    );
};

export default UnitsPage;
